import React, { useEffect, useState } from 'react'
import { translate } from 'locales'
import videoCallRatingAnalytics from 'services/analytics/VideoCallRating/videoCallRatingAnalytics'
import { ReactComponent as DissatisfiedIcon } from 'assets/icons/dissatisfied.svg'
import { ReactComponent as NeutralIcon } from 'assets/icons/neutral.svg'
import { ReactComponent as SatisfiedIcon } from 'assets/icons/satisfied.svg'
import type { VideoCallSentiment } from './services'
import { fetchAppointmentCsatEvaluation } from './services'
import * as S from './styles'

export interface VideoCallRatingPromptProps {
  appointmentId: string
  onOpenRating: (initialSentiment?: VideoCallSentiment | null) => void
}

export function VideoCallRatingPrompt({
  appointmentId,
  onOpenRating,
}: VideoCallRatingPromptProps) {
  const [visible, setVisible] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchAppointmentCsatEvaluation(appointmentId)
      .then(({ evaluation }) => {
        if (!cancelled) setVisible(!evaluation)
      })
      .catch(() => {
        if (!cancelled) setVisible(true)
      })
    return () => {
      cancelled = true
    }
  }, [appointmentId])

  useEffect(() => {
    if (visible) {
      videoCallRatingAnalytics.trackPromptView(appointmentId)
    }
  }, [visible, appointmentId])

  if (visible === null || !visible) {
    return null
  }

  return (
    <S.PromptWrap data-testid="video-call-rating-prompt">
      <S.PromptQuestion data-testid="video-call-rating-prompt-question">
        {translate('videoCallRating.question')}
      </S.PromptQuestion>
      <S.PromptFaces>
        <S.FaceButton
          type="button"
          $variant="negative"
          onClick={() => {
            videoCallRatingAnalytics.trackPromptSentimentSelected(
              appointmentId,
              'negative'
            )
            onOpenRating('negative')
          }}
          aria-label={translate('videoCallRating.sentiments.negative')}
          data-testid="video-call-rating-prompt-negative"
        >
          <DissatisfiedIcon width={36} height={36} aria-hidden />
        </S.FaceButton>
        <S.FaceButton
          type="button"
          $variant="neutral"
          onClick={() => {
            videoCallRatingAnalytics.trackPromptSentimentSelected(
              appointmentId,
              'neutral'
            )
            onOpenRating('neutral')
          }}
          aria-label={translate('videoCallRating.sentiments.neutral')}
          data-testid="video-call-rating-prompt-neutral"
        >
          <NeutralIcon width={36} height={36} aria-hidden />
        </S.FaceButton>
        <S.FaceButton
          type="button"
          $variant="positive"
          onClick={() => {
            videoCallRatingAnalytics.trackPromptSentimentSelected(
              appointmentId,
              'positive'
            )
            onOpenRating('positive')
          }}
          aria-label={translate('videoCallRating.sentiments.positive')}
          data-testid="video-call-rating-prompt-positive"
        >
          <SatisfiedIcon width={36} height={36} aria-hidden />
        </S.FaceButton>
      </S.PromptFaces>
    </S.PromptWrap>
  )
}
